import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { cn } from '@/lib/utils/helpers'
import { COMPANY_ROLES, LIBERTY_ROLES, PROPERTY_ROLES } from '@/constants/roles'

export const RESIDENT_LIST_SESSION_KEY = 'residentlist_filter'

export const RESIDENT_PER_PAGE_OPTIONS = [15, 30, 50, 100] as const

export const RESIDENT_LIST_DEFAULT_QUERY = {
  page: 1,
  perPage: 15,
  sortBy: 'asc',
  orderBy: 'first_name',
} as const

export const RESIDENT_POLICIES = {
  menu: 'resident_menu',
  detailView: 'resident_detail_view',
  listMultiEdit: 'resident_list_multi_edit',
  listExport: 'resident_list_export',
  listNotPaid: 'resident_list_not_paid',
  listPaidInFull: 'resident_list_paid_in_full',
  listCompanyView: 'resident_list_company_view',
  listPropertyView: 'resident_list_property_view',
  applicationDetailView: 'application_detail_view',
  libertyInternalView: 'resident_libertyinternal_view',
  notesWrite: 'resident_notes_write',
  propertyLibertyMessageWrite: 'resident_property_liberty_message_write',
  defaultFilesView: 'resident_default_files_view',
  defaultFilesRemove: 'resident_default_files_remove',
  /**
   * Angular `resident-edit-new` payment-review gates:
   * - L1 + L2 approval selects → `resident_default_liberty_approval`
   * - L3 / external insurance approval → `resident_default_external_approval`
   * - Delete payment → `resident_default_delete_payment`
   * Edit payment has no separate policy (Liberty role only).
   */
  defaultLibertyApproval: 'resident_default_liberty_approval',
  defaultExternalApproval: 'resident_default_external_approval',
  defaultDeletePayment: 'resident_default_delete_payment',
  mailgunLogs: 'reports_mailgun_logs',
  propertyConfigView: 'property_config_view',
  yardiConfig: 'yardi_config',
} as const

/** Mirrors Angular `resident-edit-new` `paymentStatus` plus common API tokens. */
export const PAYMENT_STATUSES = {
  Pending: 'Pending',
  Completed: 'Paid',
  Failed: 'Failed',
  Cancelled: 'Cancelled',
  PendingPaymentAuthorization: 'Pending Payment Authorization',
  ACH_Check_Pending: 'ACH Check Pending',
  ExternalPending: 'Level 3 Review',
  COMPLETED: 'Paid',
  OnHold: 'On Hold',
  Recovered: 'Recovered',
} as const

export const DOCUMENT_STATUSES = {
  Missing: 'Missing',
  Received: 'Received',
  Rejected: 'Incomplete',
  Uploaded: 'Uploaded',
} as const

export const APPLICANT_STATUSES = {
  INCOMPLETE: 'Incomplete',
  PENDING: 'Pending',
  APPROVED: 'Approved',
  DENIED: 'Denied',
  EXPIRED: 'Expired',
  VOID: 'Void',
  AWAITING: 'Awaiting',
  NOT_STARTED: 'Not Started',
  CANCELLED: 'Cancelled',
} as const

export const RELATIONSHIP_TYPES = {
  SPOUSE: 'Spouse/Partner',
  SIBLING: 'Sibling',
  CHILD: 'Child',
  PARENT: 'Parent',
  NOT_DIRECTLY_RELATED: 'Not Directly Related',
  CO_SIGNER: 'Co-signer',
} as const

export const RESIDENT_LIBERTY_ROLES = [...LIBERTY_ROLES] as readonly string[]
export const RESIDENT_COMPANY_ROLES = [...COMPANY_ROLES] as readonly string[]
export const RESIDENT_PROPERTY_ROLES = [...PROPERTY_ROLES] as readonly string[]

export const EXCLUDED_NOTE_ROLES = [
  'ROLE_EXECS',
  'ROLE_CUSTOMER_SERVICE',
  'ROLE_CUSTOMER_SERVICE_MANAGER',
  'ROLE_LIBERTY_SALES',
  'ROLE_UNDERWRITER',
  'ROLE_LIBERTY_FIN_MGR',
  'EXTERNAL_INSURANCE',
  'ROLE_REFUND_APPROVAL',
] as const

/**
 * Angular `isExcludedNoteRole` — on reimbursement public notes: hides Save and
 * makes New Message readonly (e.g. ROLE_LIBERTY_SALES). Does not hide the box.
 */
export function isExcludedNoteRole(roleName: string): boolean {
  return (EXCLUDED_NOTE_ROLES as readonly string[]).includes(roleName)
}

export const LOSS_ADJUST_REASONS = [
  { label: 'Security Deposits not used', value: 'Security Deposits not used' },
  { label: 'Partial payments taken', value: 'Partial payments taken' },
] as const

export const RESIDENT_DETAIL_TABS = [
  { id: 'residentinfo', label: 'Resident Info' },
  { id: 'default', label: 'Reimbursement' },
  { id: 'calculation', label: 'Calculation' },
  { id: 'libertyinternal', label: 'Liberty Internal' },
  { id: 'policy', label: 'Policy' },
] as const

/**
 * Roles that can edit rental_status_date (Reimbursement Request Date).
 * Mirrors the `[disabled]` rule in resident-edit-new.component.html.
 */
export const RESIDENT_STATUS_DATE_EDIT_ROLES = [
  'ROLE_ADMIN',
  'ROLE_LIBERTY_DEFAULTS',
] as const

/**
 * Mirrors Angular's `fileTypes` mapping. Used in the Document History table
 * to convert raw file_type values to human-readable labels.
 */
/**
 * Document History `type` → label map.
 * Mirrors Angular `resident-edit-new.component.ts` `fileTypes` used by the
 * Document History table (PMC + Liberty reimbursement detail).
 */
export const FILE_TYPES: Record<string, string> = {
  LEASE_AGREEMENT: 'Lease Agreement',
  PAYMENT_LEDGER: 'Payment Ledger',
  UNLAWFUL_DETAINER: 'Eviction Reimbursement',
  SKIP_CLAIM_AFFIDAVIT: 'Skip Reimbursement',
  CDC_AFFIDAVIT: 'CDC Affidavit',
  LATE_LETTER: 'Late Letter',
  JURISDICTION_EVICTION_UPDATES: 'Jurisdiction Eviction Updates',
  OTHER_SUPPORTING_DOCUMENTS: 'Other Supporting Documents',
  STATEMENT: 'Settlement Statement',
  CLAIM_SETTLEMENT_DOC: 'Settlement Statement',
  LIBERTY_DOCUMENT: 'Liberty Document',
}

/**
 * Shared `accept` for resident document uploads (claim docs, liberty, guidance).
 * Includes HEIC/HEIF alongside PDF, Office, and common image types.
 */
export const RESIDENT_DOCUMENT_FILE_ACCEPT =
  '.pdf,.doc,.docx,.png,.jpg,.jpeg,.heic,.heif,image/heic,image/heic-sequence,image/heif,.xlsx,.xls,.gif,.webp,.bmp'

/**
 * Mirrors Angular's `claimHistoryStatus` mapping for the L1/L2/L3 review log.
 */
export const CLAIM_HISTORY_STATUS: Record<string, string> = {
  claim_denied: 'Deny',
  claim_reopened: 'Re-open',
  claim_paid: 'Pay Reimbursement',
  L1_REVIEW: 'L1 Review',
}

/**
 * Mirrors Angular: roles that can interact with `Cancel Reimbursement` button.
 */
export const CANCEL_REIMBURSEMENT_ROLES = [
  'ROLE_ADMIN',
  'ROLE_LIBERTY_DEFAULTS',
  'ROLE_COMPANY_ADMIN',
  'ROLE_COMPANY_MANAGER',
  'ROLE_REGIONAL_MANAGER',
  'ROLE_PROPERTY_ADMIN',
  'ROLE_PROPERTY_MANAGER',
  'ROLE_PROPERTY_AGENT',
] as const

/** Earliest year offered in the Calculation tab "first month not paid" year dropdown. */
export const FIRST_MONTH_YEAR_START = 2015

/** Roles allowed to override the reimbursement end date on the Calculation tab. */
export const REIMBURSEMENT_END_DATE_OVERRIDE_ROLES = ['ROLE_ADMIN', 'ROLE_LIBERTY_DEFAULTS'] as const

/** Shown to non-Liberty users on the Payment Ledger once the Final Ledger is locked (Angular parity). */
export const FINAL_LEDGER_LOCKED_INFO_MESSAGE =
  'The Final Ledger has already been provided for this resident, and this field is now locked. If you need to submit additional ledger information, please contact the Reimbursements Team using the message board.'

/** Calculation tab action buttons — primary entity chrome, kept on one line. */
export const RESIDENT_CALCULATION_ACTION_BUTTON_CLASS = cn(
  primaryEntityActionButtonClass,
  'whitespace-nowrap',
)
